import { Headline } from '@oneaudi/unified-web-components';
import type { FC } from 'react';
import React from 'react';
import type { CarlineGroup } from '../../@types/mapped';
import { useContent } from '../context/ContentContext';
import type { Carline, DisplayedVehicleType } from '../utils/filter';
import { Card } from './CardCustom';
import { CardList } from './CardList';
import { NoResults } from './NoResults';
import { ResultCountHeader } from './ResultCountHeader';
import { useI18n } from '../i18n';

const getCardForCarline = (carline: Carline, index: number) => {
  const {
    identifier: { id },
    consumptionAndEmission,
  } = carline;

  return (
    <Card
      index={index}
      key={id}
      consumptionAndEmission={consumptionAndEmission?.summary || undefined}
      carline={carline}
    />
  );
};

export interface ResultsProps {
  readonly carlineGroups: CarlineGroup[];
  readonly filteredCarlines: Carline[];
  readonly hasActiveFilters: boolean;
  readonly resetAllFilters: (event: React.MouseEvent<HTMLButtonElement>) => void;
  readonly selectedVehicleType?: DisplayedVehicleType;
}

export const Results: FC<ResultsProps> = ({
  hasActiveFilters,
  carlineGroups,
  filteredCarlines,
  resetAllFilters,
  selectedVehicleType,
}) => {
  const { compareMode } = useContent();
  const hasCarlineGroups = carlineGroups.length > 0;
  const hasFilteredCarlines = filteredCarlines.length > 0;

  const renderResultCountHeader = () =>
    hasCarlineGroups && hasFilteredCarlines ? (
      <ResultCountHeader
        hasActiveFilters={hasActiveFilters}
        selectedVehicleType={selectedVehicleType}
        filteredCount={filteredCarlines.length}
      />
    ) : null;

  const noResults =
    hasActiveFilters && hasCarlineGroups && !hasFilteredCarlines ? (
      <NoResults onClick={resetAllFilters} />
    ) : null;

  const renderFilteredCards = () =>
    hasActiveFilters ? (
      <CardList>
        {filteredCarlines.map((carline, index) => getCardForCarline(carline, index))}
      </CardList>
    ) : null;

  const renderCompareStandaloneHeadline = () => (
    <Headline
      variant={{ order: '3', type: 'headline', weight: 'normal' }}
      tag="h3"
      margin={[0, 0, 'var(--spacing-relative-2xl)', 0]}
    >
      {useI18n('compare.standaloneHeadline')}
    </Headline>
  );

  const renderGroupedCards = () =>
    !hasActiveFilters
      ? carlineGroups.map((group) => (
          <React.Fragment key={group.identifier.id}>
            <Headline
              variant={{
                order: '4',
                type: 'headline',
                weight: 'normal',
              }}
              tag="h4"
              margin={[0, 0, 'var(--spacing-relative-lg)', 0]}
            >
              {group.name}
            </Headline>
            <CardList>
              {group.carlines.map((carline, index) =>
                getCardForCarline({ group: group.identifier.id, ...carline }, index),
              )}
            </CardList>
          </React.Fragment>
        ))
      : null;

  return (
    noResults || (
      <>
        {compareMode === 'compare-only' && renderCompareStandaloneHeadline()}
        {renderResultCountHeader()}
        {renderFilteredCards()}
        {renderGroupedCards()}
      </>
    )
  );
};
